import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ReactApexChart from 'react-apexcharts';

import { DataTable, EmptyState } from '../../../components/shared';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const baseChartOptions = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, background: 'transparent' },
  dataLabels: { enabled: false },
  grid: { borderColor: '#e3e8ef', strokeDashArray: 4, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } } },
  tooltip: { theme: 'light' },
};

// Fulfillment method colors (badge + chart)
const FM_COLORS = {
  FSL:          { bg: '#E0F2F1', color: '#00695C', chart: '#009688' },
  Warehouse:    { bg: '#E3F2FD', color: '#1565C0', chart: '#1565C0' },
  Supplier: { bg: '#E8EAF6', color: '#3949AB', chart: '#3949AB' },
};
const FM_LABEL = { FSL: 'FSL', Warehouse: 'Warehouse', Supplier: 'Supplier' };

function FMBadge({ method }) {
  const cfg = FM_COLORS[method] ?? { bg: '#ECEFF1', color: '#546E7A' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {FM_LABEL[method] ?? method}
      </Typography>
    </Box>
  );
}

// ─── Tab summary bar ──────────────────────────────────────────────────────────
function TabSummaryBar({ tickets }) {
  const total   = tickets.length;
  const costs   = tickets.map((t) => t.totalCost);
  const avg     = total ? costs.reduce((s, c) => s + c, 0) / total : 0;
  const highest = total ? Math.max(...costs) : 0;
  const lowest  = total ? Math.min(...costs) : 0;
  const stats = [
    { label: 'Total Tickets Costed', value: total         },
    { label: 'Avg Total Cost',        value: fmt(avg)     },
    { label: 'Highest Cost Ticket',   value: fmt(highest) },
    { label: 'Lowest Cost Ticket',    value: fmt(lowest)  },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 3, px: 3, py: 2, borderBottom: '1px solid var(--grey-200)' }}>
      {stats.map((s) => (
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--grey-900)' }}>{s.value}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────
export default function CostPerTicketTab({ ticketCosts }) {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ field: 'totalCost', direction: 'desc' });

  const handleSort = (field) => setSortConfig((prev) => ({
    field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
  }));

  const sorted = useMemo(
    () =>
      [...ticketCosts].sort((a, b) => {
        const av = a[sortConfig.field] ?? '';
        const bv = b[sortConfig.field] ?? '';
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }),
    [ticketCosts, sortConfig],
  );

  // Chart: bar by ticket, colored by fulfillment method
  const chartCategories = ticketCosts.map((t) => t.ticketNumber);
  const chartColors     = ticketCosts.map((t) => FM_COLORS[t.fulfillmentMethod]?.chart ?? '#607d8b');
  const chartData       = ticketCosts.map((t) => parseFloat(t.totalCost.toFixed(2)));

  const chartOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar' },
    xaxis: {
      categories: chartCategories,
      labels: { style: { fontSize: '10px', colors: '#607d8b' } },
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: '#607d8b' },
        formatter: (v) => `$${v.toFixed(0)}`,
      },
    },
    colors: chartColors,
    plotOptions: { bar: { distributed: true, borderRadius: 4, columnWidth: '50%' } },
    legend: { show: false },
    tooltip: { y: { formatter: (v) => fmt(v) } },
  };
  const chartSeries = [{ name: 'Total Cost', data: chartData }];

  const columns = [
    {
      field: 'ticketNumber',
      headerName: 'Ticket #',
      sortable: true,
      renderCell: (row) => (
        <Typography
          onClick={(e) => { e.stopPropagation(); navigate(`/orders/parts/${row.partOrderId}`); }}
          sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.ticketNumber}
        </Typography>
      ),
    },
    { field: 'partNumber',   headerName: 'Part #',   sortable: true },
    { field: 'customerName', headerName: 'Customer', sortable: true },
    {
      field: 'partCost',
      headerName: 'Part Cost',
      sortable: true,
      renderCell: (row) => <Typography sx={{ fontSize: '0.875rem' }}>{fmt(row.partCost)}</Typography>,
    },
    {
      field: 'shippingCost',
      headerName: 'Shipping Cost',
      sortable: true,
      renderCell: (row) => <Typography sx={{ fontSize: '0.875rem' }}>{fmt(row.shippingCost)}</Typography>,
    },
    {
      field: 'totalCost',
      headerName: 'Total Cost',
      sortable: true,
      renderCell: (row) => (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>{fmt(row.totalCost)}</Typography>
      ),
    },
    {
      field: 'fulfillmentMethod',
      headerName: 'Fulfillment',
      sortable: true,
      renderCell: (row) => <FMBadge method={row.fulfillmentMethod} />,
    },
  ];

  return (
    <>
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          Cost per Ticket
        </Typography>
      </Box>

      <TabSummaryBar tickets={ticketCosts} />

      {ticketCosts.length === 0 ? (
        <EmptyState title="No ticket costs" description="Try adjusting your date range." />
      ) : (
        <>
          {/* Chart */}
          <Box sx={{ px: 3, pt: 2, pb: 1, borderBottom: '1px solid var(--grey-200)' }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: 1 }}>
              Total Cost by Ticket
            </Typography>
            <ReactApexChart
              type="bar"
              options={chartOptions}
              series={chartSeries}
              height={180}
              width="100%"
            />
            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              {Object.entries(FM_LABEL).map(([key, label]) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: FM_COLORS[key]?.chart }} />
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-600)' }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mx: 3, mt: 3, mb: 3, border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
            <DataTable columns={columns} data={sorted} sortConfig={sortConfig} onSort={handleSort} />
          </Box>
        </>
      )}
    </>
  );
}
